import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Users,
  Settings,
  DollarSign,
  Package,
  Heart,
  Briefcase,
  ShieldCheck,
  ArrowUpRight,
  ArrowDownRight,
  Activity
} from 'lucide-react';
import { syncManager } from '../services/sync';
import { wsClient } from '../services/WebSocketClient';
import { config } from '../config';
import { businessKPIs } from '../services/BusinessKPIs';
import { formatZAR, formatPercent } from '../utils/formatters';

interface KPIData {
  value: string | number;
  change: number;
  trend: 'up' | 'down';
  label: string;
  isCurrency: boolean;
  isPercent: boolean;
}

interface OperationalArea {
  id: string;
  name: string;
  icon: React.ReactNode;
  kpiKeys: { key: string; label: string; isCurrency: boolean; isPercent: boolean; defaultTrend: 'up' | 'down' }[];
}

const OPERATIONAL_AREAS: OperationalArea[] = [
  {
    id: 'marketing',
    name: 'Marketing',
    icon: <Users size={20} />,
    kpiKeys: [
      { key: 'leadGeneration', label: 'Lead Gen', isCurrency: false, isPercent: false, defaultTrend: 'up' },
      { key: 'costPerLead', label: 'CPA', isCurrency: true, isPercent: false, defaultTrend: 'down' },
    ]
  },
  {
    id: 'sales',
    name: 'Sales',
    icon: <TrendingUp size={20} />,
    kpiKeys: [
      { key: 'conversionRate', label: 'Conversion', isCurrency: false, isPercent: true, defaultTrend: 'up' },
      { key: 'averageDealSize', label: 'Avg Deal', isCurrency: true, isPercent: false, defaultTrend: 'up' },
    ]
  },
  {
    id: 'operations',
    name: 'Ops & Product',
    icon: <Package size={20} />,
    kpiKeys: [
      { key: 'processEfficiency', label: 'Efficiency', isCurrency: false, isPercent: true, defaultTrend: 'up' },
      { key: 'qualityScore', label: 'Quality', isCurrency: false, isPercent: false, defaultTrend: 'up' },
    ]
  },
  {
    id: 'fulfillment',
    name: 'Order Fulfillment',
    icon: <Activity size={20} />,
    kpiKeys: [
      { key: 'onTimeDelivery', label: 'On-Time %', isCurrency: false, isPercent: true, defaultTrend: 'up' },
      { key: 'taskCompletionRate', label: 'Completion', isCurrency: false, isPercent: true, defaultTrend: 'up' },
    ]
  },
  {
    id: 'customer',
    name: 'Customer Relations',
    icon: <Heart size={20} />,
    kpiKeys: [
      { key: 'customerSatisfaction', label: 'CSAT', isCurrency: false, isPercent: false, defaultTrend: 'up' },
      { key: 'customerChurnRate', label: 'Churn', isCurrency: false, isPercent: true, defaultTrend: 'down' },
    ]
  },
  {
    id: 'admin',
    name: 'Admin & Exec',
    icon: <ShieldCheck size={20} />,
    kpiKeys: [
      { key: 'operationalHealth', label: 'Health Score', isCurrency: false, isPercent: false, defaultTrend: 'up' },
      { key: 'budgetVariance', label: 'Budget Var', isCurrency: true, isPercent: false, defaultTrend: 'down' },
    ]
  },
  {
    id: 'finance',
    name: 'Finance Management',
    icon: <DollarSign size={20} />,
    kpiKeys: [
      { key: 'cashFlow', label: 'Cash Flow', isCurrency: true, isPercent: false, defaultTrend: 'up' },
      { key: 'profitMargin', label: 'Profit Margin', isCurrency: false, isPercent: true, defaultTrend: 'up' },
    ]
  },
  {
    id: 'strategy',
    name: 'Strategic Planning',
    icon: <Briefcase size={20} />,
    kpiKeys: [
      { key: 'revenueGrowth', label: 'Rev Growth', isCurrency: false, isPercent: true, defaultTrend: 'up' },
      { key: 'runway', label: 'Runway', isCurrency: false, isPercent: false, defaultTrend: 'up' },
    ]
  },
];

const DashboardView: React.FC = () => {
  const [kpiState, setKpiState] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(true);
  const wsUpdateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced WS update handler to prevent rapid re-renders during reconnection
  const handleWsUpdate = useCallback((payload: any) => {
    if (wsUpdateTimer.current) {clearTimeout(wsUpdateTimer.current);}
    wsUpdateTimer.current = setTimeout(() => {
      setKpiState(prev => ({
        ...prev,
        ...payload
      }));
    }, 150); // 150ms debounce
  }, []);

  useEffect(() => {
    const initDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Try to load the most recent cached data first to eliminate flickering
        const cachedData = await businessKPIs.getCachedKPIs();
        if (cachedData) {
          setKpiState(cachedData);
          setLoading(false); // Hide loader immediately if cache exists
        }

        // 2. Fetch fresh data from the API/Service
        const data = await businessKPIs.getAllKPIs();
        setKpiState(prev => ({
          ...prev,
          ...data
        }));
      } catch (err) {
        console.error('Failed to load Dashboard KPIs:', err);
        setError('Unable to load live data. Showing defaults.');
      } finally {
        setLoading(false);
      }
    };

    initDashboard();

    const handleConnected = () => setWsConnected(true);
    const handleDisconnected = () => setWsConnected(false);

    wsClient.on('DASHBOARD_UPDATE', handleWsUpdate);
    wsClient.on('connected', handleConnected);
    wsClient.on('disconnected', handleDisconnected);

    return () => {
      wsClient.off('DASHBOARD_UPDATE', handleWsUpdate);
      wsClient.off('connected', handleConnected);
      wsClient.off('disconnected', handleDisconnected);
      if (wsUpdateTimer.current) {clearTimeout(wsUpdateTimer.current);}
    };
  }, [handleWsUpdate]);

  const formatValue = (val: any, kpiConfig: any) => {
    if (val === undefined || val === null) {return '0';}
    if (kpiConfig.isCurrency) {return formatZAR(val);}
    if (kpiConfig.isPercent) {return formatPercent(val);}
    return val.toString();
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-3xl font-black text-slate-800 uppercase tracking-tighter">
          Command Center
        </h1>
        <p className="text-slate-500 font-medium text-sm">
          Real-time business intelligence across all operational areas.
          {!wsConnected && (
            <span className="ml-2 text-amber-600 font-bold">· Reconnecting…</span>
          )}
        </p>
      </header>

      {loading && (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {error && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <p className="text-amber-700 text-sm font-medium">{error}</p>
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {OPERATIONAL_AREAS.map((area) => {
            // Determine which strategic category to use for this area
            const categoryMap: Record<string, string> = {
              marketing: 'marketing',
              sales: 'sales',
              operations: 'operations',
              fulfillment: 'operations',
              customer: 'customer',
              admin: 'operations',
              finance: 'finance',
              strategy: 'finance',
            };

            const category = categoryMap[area.id];
            const categoryData = kpiState?.strategic?.[category] || {};

            return (
              <motion.div
                key={area.id}
                whileHover={{ y: -4 }}
                className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 flex flex-col gap-4"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                    {area.icon}
                  </div>
                  <h3 className="font-bold text-slate-800 text-sm uppercase tracking-tight">
                    {area.name}
                  </h3>
                </div>

                <div className="space-y-4">
                  {area.kpiKeys.map((kpi, idx) => {
                    const realValue = categoryData[kpi.key] ?? 0;
                    // Note: Change/Trend are not explicitly provided by BusinessKPIs service in a simple way,
                    // defaulting to 0/up unless we implement historical comparison.
                    const change = 0;
                    const trend = kpi.defaultTrend;

                    return (
                      <div key={idx} className="flex items-end justify-between">
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            {kpi.label}
                          </p>
                          <p className="text-2xl font-black text-slate-800 leading-none">
                            {formatValue(realValue, kpi)}
                          </p>
                        </div>
                        <div className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${
                          trend === 'up' ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50'
                        }`}>
                          {trend === 'up' ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                          {Math.abs(change)}%
                        </div>
                      </div>
                    );
                  })}
              </div>
            </motion.div>
          );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardView;
